import { ChangeDetectorRef, Component, DestroyRef, ElementRef, HostListener, OnInit } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';
import { Subject, of } from 'rxjs';
import { catchError, debounceTime, distinctUntilChanged, switchMap, tap } from 'rxjs/operators';
import { SocialFeedService } from '../../services/social-feed.service';
import { SocialAuthor, SocialPost, SocialSearchGroup, SocialSearchResults } from '../../models/social-feed.model';

const MIN_LENGTH = 2;

/**
 * Search box for people, groups and posts. Types-ahead after a short pause
 * (older requests are cancelled), shows a few results of each kind in a
 * dropdown, and opens the clicked result. Access rules are enforced by the
 * API — this only displays what it returns.
 */
@Component({
  selector: 'app-social-search',
  templateUrl: './social-search.component.html',
  styleUrls: ['./social-search.component.scss'],
  standalone: false,
})
export class SocialSearchComponent implements OnInit {
  query = '';
  open = false;
  loading = false;
  failed = false;
  results: SocialSearchResults | null = null;

  private terms = new Subject<string>();

  constructor(
    private socialFeedService: SocialFeedService,
    private router: Router,
    private cdr: ChangeDetectorRef,
    private destroyRef: DestroyRef,
    private host: ElementRef<HTMLElement>
  ) {}

  get hasQuery(): boolean {
    return this.clean(this.query).length >= MIN_LENGTH;
  }

  get isEmpty(): boolean {
    return !!this.results && !this.results.people.length && !this.results.groups.length && !this.results.posts.length;
  }

  ngOnInit(): void {
    this.terms
      .pipe(
        debounceTime(300),
        distinctUntilChanged(),
        tap((q) => {
          if (q.length < MIN_LENGTH) {
            this.results = null;
            this.loading = false;
            this.cdr.detectChanges();
          }
        }),
        // switchMap drops the previous request if the text changes again.
        switchMap((q) =>
          q.length < MIN_LENGTH
            ? of(null)
            : this.socialFeedService.search(q).pipe(
                tap(() => (this.failed = false)),
                catchError(() => {
                  this.failed = true;
                  return of(null);
                })
              )
        ),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe((results) => {
        this.results = results;
        this.loading = false;
        this.cdr.detectChanges();
      });
  }

  onInput(value: string): void {
    this.query = value;
    this.open = true;
    this.loading = this.hasQuery;
    this.terms.next(this.clean(value));
  }

  clear(): void {
    this.query = '';
    this.results = null;
    this.loading = false;
    this.failed = false;
    this.terms.next('');
  }

  openFirst(): void {
    if (!this.results) {
      return;
    }
    if (this.results.people.length) {
      this.openPerson(this.results.people[0]);
    } else if (this.results.groups.length) {
      this.openGroup(this.results.groups[0]);
    } else if (this.results.posts.length) {
      this.openPost(this.results.posts[0]);
    }
  }

  openPerson(person: SocialAuthor): void {
    this.go(['/social-profile', person.id]);
  }

  openGroup(group: SocialSearchGroup): void {
    if (group.kind === 'community') {
      this.go(['/communities', group.slug]);
      return;
    }
    this.go([group.kind === 'map' ? '/social-group' : '/social-game-group', group.id]);
  }

  openPost(post: SocialPost): void {
    this.go(['/social-post', post.id]);
  }

  snippet(post: SocialPost): string {
    const text = (post.content || '').trim();
    return text.length > 90 ? text.slice(0, 87) + '…' : text;
  }

  initials(name: string): string {
    return (name || '')
      .split(/\s+/)
      .map((w) => w[0])
      .filter(Boolean)
      .slice(0, 2)
      .join('')
      .toUpperCase();
  }

  avatarUrl(path: string | null): string {
    return this.socialFeedService.resolveImageUrl(path);
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    if (this.open && !this.host.nativeElement.contains(event.target as Node)) {
      this.open = false;
    }
  }

  @HostListener('document:keydown.escape')
  onEscape(): void {
    this.open = false;
  }

  private clean(value: string): string {
    return (value || '').trim().replace(/\s+/g, ' ');
  }

  private go(commands: any[]): void {
    this.open = false;
    this.clear();
    this.router.navigate(commands);
  }
}
